import { useState, useMemo, useCallback } from 'react';

/**
 * 자동완성 훅
 * @param {Object} options 자동완성 옵션
 * @param {Array} options.items 자동완성 대상 아이템들
 * @param {Array} options.searchKeys 검색할 키들
 * @param {number} options.maxResults 최대 결과 수
 * @param {number} options.minSearchLength 최소 검색 길이
 * @param {Function} options.filterFn 커스텀 필터 함수
 * @param {Function} options.mapFn 커스텀 맵핑 함수
 */
export function useAutocomplete({
	items,
	searchKeys,
	maxResults = 10,
	minSearchLength = 1,
	filterFn,
	mapFn,
}) {
	const [searchText, setSearchText] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);

	// 기본 필터 함수
	const defaultFilterFn = useCallback(
		(item, search) => {
			const searchLower = search.toLowerCase();
			return searchKeys.some((key) => {
				const value = item[key];
				if (typeof value === 'string') {
					return value.toLowerCase().includes(searchLower);
				}
				if (Array.isArray(value)) {
					return value.some(
						(v) =>
							typeof v === 'string' && v.toLowerCase().includes(searchLower),
					);
				}
				return false;
			});
		},
		[searchKeys],
	);

	// 기본 맵핑 함수
	const defaultMapFn = useCallback(
		(item) => {
			const firstKey = searchKeys[0];
			const value = item[firstKey];
			const label = typeof value === 'string' ? value : String(value);

			return {
				id: Math.random().toString(36).substr(2, 9),
				label,
				value: label,
				data: item,
			};
		},
		[searchKeys],
	);

	// 필터링된 항목들
	const filteredItems = useMemo(() => {
		if (!searchText.trim() || searchText.length < minSearchLength) {
			return [];
		}

		const filter = filterFn || defaultFilterFn;
		const mapper = mapFn || defaultMapFn;

		return items
			.filter((item) => filter(item, searchText))
			.slice(0, maxResults)
			.map((item) => mapper(item, searchText));
	}, [
		items,
		searchText,
		minSearchLength,
		maxResults,
		filterFn,
		mapFn,
		defaultFilterFn,
		defaultMapFn,
	]);

	// 입력 변경 핸들러
	const handleInputChange = useCallback(
		(e) => {
			const value = e.target.value;
			setSearchText(value);
			setIsOpen(value.trim().length >= minSearchLength);
			setSelectedIndex(-1);
		},
		[minSearchLength],
	);

	// 아이템 선택
	const selectItem = useCallback((item) => {
		setSearchText(item.value);
		setIsOpen(false);
		setSelectedIndex(-1);
	}, []);

	// 인덱스로 아이템 선택
	const selectByIndex = useCallback(
		(index) => {
			if (index >= 0 && index < filteredItems.length) {
				selectItem(filteredItems[index]);
			}
		},
		[filteredItems, selectItem],
	);

	// 키보드 이벤트 핸들러
	const handleKeyDown = useCallback(
		(e) => {
			if (!isOpen || filteredItems.length === 0) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) =>
						Math.min(prev + 1, filteredItems.length - 1),
					);
					break;

				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex((prev) => Math.max(prev - 1, -1));
					break;

				case 'Enter':
					e.preventDefault();
					if (selectedIndex >= 0 && filteredItems[selectedIndex]) {
						selectByIndex(selectedIndex);
					}
					break;

				case 'Tab':
					if (selectedIndex >= 0 && filteredItems[selectedIndex]) {
						e.preventDefault();
						selectByIndex(selectedIndex);
					}
					break;

				case 'Escape':
					setIsOpen(false);
					setSelectedIndex(-1);
					break;
			}
		},
		[isOpen, filteredItems, selectedIndex, selectByIndex],
	);

	// 블러 핸들러
	const handleInputBlur = useCallback(() => {
		// 클릭 이벤트가 처리될 수 있도록 지연
		setTimeout(() => {
			setIsOpen(false);
			setSelectedIndex(-1);
		}, 200);
	}, []);

	// 아이템 클릭 핸들러
	const handleItemClick = useCallback(
		(item) => {
			selectItem(item);
		},
		[selectItem],
	);

	// 아이템 호버 핸들러
	const handleItemHover = useCallback((index) => {
		setSelectedIndex(index);
	}, []);

	// 리셋
	const reset = useCallback(() => {
		setSearchText('');
		setIsOpen(false);
		setSelectedIndex(-1);
	}, []);

	// 선택된 아이템 가져오기
	const getSelectedItem = useCallback(() => {
		if (selectedIndex >= 0 && selectedIndex < filteredItems.length) {
			return filteredItems[selectedIndex];
		}
		return null;
	}, [selectedIndex, filteredItems]);

	return {
		// 상태
		searchText,
		isOpen,
		selectedIndex,
		filteredItems,

		// 액션
		setSearchText,
		setIsOpen,
		setSelectedIndex,
		selectItem,
		selectByIndex,

		// 이벤트 핸들러
		handleInputChange,
		handleKeyDown,
		handleInputBlur,
		handleItemClick,
		handleItemHover,

		// 유틸리티
		reset,
		getSelectedItem,
	};
}
